/**
 * RecentImportsList — AK-369 «Последние импорты» в панели «+ Поле».
 *
 * Живёт внутри import-card AddFieldPanel (под дропзоной, фаза armed). Грузит
 * GET /fields/import/batches и показывает последние пачки импорта с откатом:
 *   - строка: N полей · дата · автор + [Отменить импорт];
 *   - откат (rollback) → line-through + [Восстановить] (в рамках сессии);
 *   - восстановление (restore) → снимает line-through.
 *
 * Откат/восстановление обратимы (архив полей, как AK-340) — поэтому пачка не
 * исчезает мгновенно из UI: держим оптимистичный `rolled_back` флаг на строке.
 * После MVT-инвалидации поля пропадают/появляются на карте сразу.
 *
 * Бэк ещё не готов (ветка feat/ak-369-batch-rollback / PR #414) → listImportBatches
 * глотает 404 и возвращает []; тогда раздел просто НЕ рисуется (graceful).
 *
 * DS: только @/components/ui/* + Lucide + Earth Tech токены. Ноль hex.
 */
import { useCallback, useEffect, useState } from 'react';
import { History, Undo2, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  listImportBatches,
  rollbackImportBatch,
  restoreImportBatch,
  type ImportBatch,
} from '@/lib/map/import/importApi';

interface RecentImportsListProps {
  /** После успешного отката/восстановления — v2.tsx делает MVT-инвалидацию. */
  onChanged: () => void;
}

/** «N полей» русский плюрал. */
function pluralFields(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} поле`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} поля`;
  return `${n} полей`;
}

/** ISO → «6 июл, 13:47» (короткая локаль). Пустая/битая дата → ''. */
function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function RecentImportsList({ onChanged }: RecentImportsListProps) {
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [loading, setLoading] = useState(true);
  // batch_id, по которому сейчас идёт откат/восстановление (дизейбл кнопки).
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void listImportBatches().then((bs) => {
      if (alive) {
        setBatches(bs);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  const handleRollback = useCallback(
    async (batchId: string) => {
      setBusyId(batchId);
      try {
        await rollbackImportBatch(batchId);
        setBatches((prev) =>
          prev.map((b) => (b.batch_id === batchId ? { ...b, rolled_back: true } : b)),
        );
        onChanged();
      } catch {
        // тихо — строка остаётся кликабельной для повтора
      } finally {
        setBusyId(null);
      }
    },
    [onChanged],
  );

  const handleRestore = useCallback(
    async (batchId: string) => {
      setBusyId(batchId);
      try {
        await restoreImportBatch(batchId);
        setBatches((prev) =>
          prev.map((b) => (b.batch_id === batchId ? { ...b, rolled_back: false } : b)),
        );
        onChanged();
      } catch {
        // тихо
      } finally {
        setBusyId(null);
      }
    },
    [onChanged],
  );

  // Пока грузим — ничего (нет мерцания пустой шапки). Пусто → раздел не рисуется.
  if (loading || batches.length === 0) return null;

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="recent-imports">
      <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
        <History size={14} />
        Последние импорты
      </div>
      <div className="flex flex-col gap-1">
        {batches.map((b) => {
          const busy = busyId === b.batch_id;
          return (
            <div
              key={b.batch_id}
              data-testid={`recent-import-${b.batch_id}`}
              data-rolled-back={b.rolled_back ? 'true' : undefined}
              className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent/50"
            >
              <div className="min-w-0 flex-1">
                <div
                  className={
                    'text-xs font-semibold truncate ' +
                    (b.rolled_back ? 'line-through text-muted-foreground' : 'text-foreground')
                  }
                >
                  {pluralFields(b.count)}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {[formatDate(b.created_at), b.author].filter(Boolean).join(' · ')}
                </div>
              </div>
              {b.rolled_back ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs flex-none"
                  data-testid={`recent-import-restore-${b.batch_id}`}
                  disabled={busy}
                  onClick={() => void handleRestore(b.batch_id)}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  Восстановить
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs flex-none text-muted-foreground hover:text-destructive"
                  data-testid={`recent-import-rollback-${b.batch_id}`}
                  disabled={busy}
                  onClick={() => void handleRollback(b.batch_id)}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}
                  Отменить
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
